import { getTenantUnits } from '@core/configuration';

import { type UnitEnumValue } from '../types/api/generated-units';
import { hasStringValue, hasValue } from './equality';
import { getUnitLabel } from './unitHelpers';

/**
 * Converts the provided string to a finite, non-NaN Number. If the string does
 * not consist of a finite, non-NaN number, this function returns the provided
 * default value instead.
 *
 * This is intended for use with programmatically serialized decimal values,
 * not user input.
 */
export const parseDecimalToNumber = (
  numericString: string | undefined | null,
  defaultValue: number = 0,
): number => {
  const number = Number(numericString);
  if (!Number.isFinite(number)) {
    return defaultValue;
  }
  return number;
};

/**
 * Abbreviates a number to a string with a k or m suffix.
 * @param value - The number to abbreviate.
 * @returns The abbreviated number as a string.
 */
export const abbreviateNumber = (value: number | string): string => {
  const num = parseFloat(value.toString());
  const absNum = Math.abs(num);
  if (Number.isNaN(num)) return '-';
  let div = 1;
  let suffix = '';
  if (absNum >= 1000 && absNum < 1000000) {
    div = 1000;
    suffix = 'K';
  } else if (absNum >= 1000000) {
    div = 1000000;
    suffix = 'M';
  }

  const frac = Math.trunc(absNum / div) < 10 ? 1 : 0;
  const resultNum = (num / div).toFixed(frac).replace(/\.0$/, '');
  return `${resultNum}${suffix}`;
};

export const formatSignedPercent = (v?: string | number): string | undefined => {
  if (!hasValue(v)) return undefined;
  const n = Number(v);
  if (Number.isNaN(n)) return undefined;
  const shown = Number.isInteger(n) ? n.toFixed(0) : n.toFixed(1);
  return `${n > 0 ? '+' : ''}${shown}%`;
};

export const formatQuantityWithUnit = (quantity: string, unit: UnitEnumValue): string => {
  const numValue = parseDecimalToNumber(quantity);
  if (numValue === 0) return '';
  const label = getUnitLabel(unit);
  const suffix = hasStringValue(label) ? ` ${label}` : '';
  return `${numValue.toLocaleString(getTenantUnits().locale, { maximumFractionDigits: 2 })}${suffix}`;
};

export const addDecimalStrings = (a: string, b: string): string =>
  String(parseDecimalToNumber(a) + parseDecimalToNumber(b));

// Returns a normalized decimal string by removing thousands separators and
// trailing decimals. Empty, whitespace-only, null, or undefined inputs return null.
export const cleanedNullableDecimal = (s: string | null | undefined): string | null =>
  hasStringValue(s) ? cleanedDecimal(s) : null;

// Returns a normalized decimal string by removing thousands separators and
// trailing decimals. Empty, whitespace-only, null, or undefined inputs return ''.
export const cleanedDecimal = (s: string | null | undefined): string => {
  if (!hasStringValue(s)) {
    return '';
  }

  const cleaned = s.trim().replaceAll(',', '');

  if (cleaned.indexOf('.') === cleaned.length - 1) {
    return cleaned.slice(0, -1);
  } else {
    return cleaned;
  }
};

// Parses and formats a decimal string by optionally rounding to a maximum number
// of fractional digits and removing insignificant trailing zeroes.
// Returns an empty string for null, empty, or non-numeric input.
// Examples: "1.23000" -> "1.23"   "123.000" -> "123", "1.23456" (maxFractionDigits = 2) -> "1.23", "2,500" -> "2500"
export const formatDecimalString = (
  value: number | string | null | undefined,
  maxFractionDigits?: number,
): string => {
  if (!hasValue(value)) {
    return '';
  }

  if (typeof value === 'string' && value.trim() === '') {
    return '';
  }

  const numeric = typeof value === 'number' ? value : Number(value.replaceAll(',', ''));
  if (!Number.isFinite(numeric)) {
    return '';
  }

  const formatted =
    maxFractionDigits !== undefined ? numeric.toFixed(maxFractionDigits) : String(numeric);

  return formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

// ratioToPercent converts a ratio 0.123 to a human-readable percents 12.3 (%) with 3 digits after comma and without trailing zeroes
// if the value is empty (null, undefined or ''), returns the specified empty value ('' by default)
export const ratioToPercent = (
  ratio: string | null | undefined,
  emptyValue: string = '',
): string => {
  if (!hasValue(ratio)) {
    return emptyValue;
  }
  const parsed = parseFloat(ratio);
  if (isNaN(parsed)) {
    return emptyValue;
  }
  return formatDecimalString((parsed * 100).toFixed(3));
};

// nullablePercentToRatio converts a percentage in the human-readable format of 12.3 (%) into 0.123 with 6 digits after comma without trailing zeroes.
// if the value is empty (null, undefined or ''), returns the specified empty value ('0' by default)
export const percentToRatio = (
  percent: string | null | undefined,
  emptyValue: string = '',
): string => {
  if (!hasValue(percent)) {
    return emptyValue;
  }
  const parsed = parseFloat(percent);
  if (isNaN(parsed)) {
    return emptyValue;
  }
  return formatDecimalString((parsed / 100).toFixed(6));
};

export const escalationPercentToChangeRatio = (percent: string | null): string | null => {
  const parsed = parseFloat((percent ?? '').replaceAll(',', ''));
  return Number.isNaN(parsed) ? null : (parsed / 100).toFixed(3);
};

/**
 * Checks if a nullable number or string value is empty.
 * Returns true if the value is null, undefined, or an empty/whitespace string.
 */
export const isNullableNumberOrStringEmpty = (value: number | string | null | undefined): boolean =>
  !hasValue(value) || (typeof value === 'string' && !hasStringValue(value));
